import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import usePushNotifications from '@/hooks/usePushNotifications';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Bell,
  BellOff,
  BellRing,
  Check,
  X,
  Loader2,
  AlertTriangle,
  Info,
  Send,
  Shield
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationSettings = ({ compact = false }) => {
  const { token } = useAuth();
  const {
    supported,
    permission,
    isSubscribed,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification
  } = usePushNotifications();

  const [preferences, setPreferences] = useState({
    critical_alerts: true,
    high_alerts: true,
    medium_alerts: false,
    low_alerts: false,
    weather_alerts: true,
    earthquake_alerts: true,
    crime_alerts: true,
    traffic_alerts: false,
    quiet_hours_start: null,
    quiet_hours_end: null,
    quiet_hours_critical_override: true
  });
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [testSent, setTestSent] = useState(false);

  // Load preferences
  useEffect(() => {
    const loadPreferences = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API_URL}/api/notifications/preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data) {
          setPreferences(prev => ({ ...prev, ...res.data }));
        }
      } catch (e) {
        console.error('Failed to load notification preferences:', e);
      }
    };
    loadPreferences();
  }, [token]);

  // Save preferences
  const savePreferences = async () => {
    if (!token) return;
    setSavingPrefs(true);
    try {
      await axios.put(
        `${API_URL}/api/notifications/preferences`,
        preferences,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {
      console.error('Failed to save preferences:', e);
    } finally {
      setSavingPrefs(false);
    }
  };

  // Toggle preference
  const togglePref = (key) => {
    setPreferences(prev => {
      const newPrefs = { ...prev, [key]: !prev[key] };
      // Auto-save after toggle
      setTimeout(() => savePreferences(), 100);
      return newPrefs;
    });
  };

  // Handle test notification
  const handleTest = async () => {
    const sent = await sendTestNotification();
    if (sent) {
      setTestSent(true);
      setTimeout(() => setTestSent(false), 3000);
    }
  };

  // Not supported
  if (!supported) {
    return (
      <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
        <div className="flex items-center gap-2 text-slate-400">
          <BellOff className="w-5 h-5" />
          <span>Push notifications are not supported in this browser</span>
        </div>
      </div>
    );
  }

  // Compact version for quick toggle
  if (compact) {
    return (
      <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
        <div className="flex items-center gap-3">
          {isSubscribed ? (
            <BellRing className="w-5 h-5 text-teal-400" />
          ) : (
            <BellOff className="w-5 h-5 text-slate-500" />
          )}
          <div>
            <p className="text-sm font-medium text-slate-200">Push Notifications</p>
            <p className="text-xs text-slate-500">
              {isSubscribed ? 'Receiving alerts' : 'Enable to receive alerts'}
            </p>
          </div>
        </div>
        <Button
          variant={isSubscribed ? "outline" : "default"}
          size="sm"
          onClick={isSubscribed ? unsubscribe : subscribe}
          disabled={loading}
          className={isSubscribed ? 'border-slate-600' : 'bg-teal-600 hover:bg-teal-500'}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isSubscribed ? (
            'Disable'
          ) : (
            'Enable'
          )}
        </Button>
      </div>
    );
  }

  // Full settings panel
  return (
    <div className="space-y-6" data-testid="notification-settings">
      {/* Main Toggle */}
      <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              isSubscribed ? 'bg-teal-500/20' : 'bg-slate-700'
            }`}>
              {isSubscribed ? (
                <BellRing className="w-6 h-6 text-teal-400" />
              ) : (
                <BellOff className="w-6 h-6 text-slate-500" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100">Push Notifications</h3>
              <p className="text-sm text-slate-400">
                {permission === 'denied' 
                  ? 'Notifications blocked by browser'
                  : isSubscribed 
                    ? 'You will receive real-time alerts'
                    : 'Enable to receive critical alerts instantly'
                }
              </p>
            </div>
          </div>
          
          {permission === 'denied' ? (
            <Badge variant="destructive" className="flex items-center gap-1">
              <X className="w-3 h-3" />
              Blocked
            </Badge>
          ) : (
            <Button
              onClick={isSubscribed ? unsubscribe : subscribe}
              disabled={loading}
              className={isSubscribed 
                ? 'bg-slate-700 hover:bg-slate-600' 
                : 'bg-teal-600 hover:bg-teal-500'
              }
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : isSubscribed ? (
                <BellOff className="w-4 h-4 mr-2" />
              ) : (
                <Bell className="w-4 h-4 mr-2" />
              )}
              {isSubscribed ? 'Disable' : 'Enable Notifications'}
            </Button>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="mt-3 flex items-center gap-2 text-red-400 text-sm">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        )}

        {/* Permission denied help */}
        {permission === 'denied' && (
          <div className="mt-4 p-3 rounded-lg bg-amber-950/50 border border-amber-900/50">
            <div className="flex items-start gap-2 text-amber-400 text-sm">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>
                Notifications are blocked. To enable them, click the lock icon in your browser's address bar and allow notifications for this site.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Alert Type Preferences */}
      {isSubscribed && (
        <>
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Shield className="w-4 h-4 text-teal-400" />
              Alert Types
            </h4>
            
            <div className="space-y-2">
              {[
                { key: 'critical_alerts', label: 'Critical Alerts', desc: 'Immediate threats', required: true },
                { key: 'high_alerts', label: 'High Priority', desc: 'Urgent situations' },
                { key: 'medium_alerts', label: 'Medium Priority', desc: 'General alerts' },
                { key: 'weather_alerts', label: 'Weather Alerts', desc: 'Severe weather warnings' },
                { key: 'earthquake_alerts', label: 'Earthquake Alerts', desc: 'Seismic activity' },
                { key: 'crime_alerts', label: 'Crime Alerts', desc: 'Local crime reports' },
                { key: 'traffic_alerts', label: 'Traffic Alerts', desc: 'Major incidents' },
              ].map(({ key, label, desc, required }) => (
                <div 
                  key={key}
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-800/30 border border-slate-700/50"
                >
                  <div>
                    <p className="text-sm font-medium text-slate-200">{label}</p>
                    <p className="text-xs text-slate-500">{desc}</p>
                  </div>
                  <Switch
                    checked={preferences[key]}
                    onCheckedChange={() => togglePref(key)}
                    disabled={required}
                    className="data-[state=checked]:bg-teal-600"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Test Notification */}
          <div className="pt-4 border-t border-slate-700">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={testSent}
              className="w-full border-slate-700 hover:bg-slate-800"
            >
              {testSent ? (
                <>
                  <Check className="w-4 h-4 mr-2 text-green-400" />
                  Test Sent!
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Test Notification
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationSettings;
